/**
 * Edit Profile Dialog - Popup for editing user profile
 *
 * Matches Xamarin EditProfileMobilePage.xaml behavior:
 * - Pre-fills fields from Redux/IndexedDB user data
 * - Phone number is NOT editable
 * - Carrier single-select dropdown
 * - Plant multi-select dropdown with search
 * - Change detection enables/disables Update button
 * - Prevents multiple submissions

 */

import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, useToast } from '@/components/ui';
import { useCarriersQuery, usePlantsQuery } from '@/hooks/auth';
import { updateUserProfile } from '@/service/AuthenticationService';
import { UpdateUserProfileRequestDTO, type CarrierDTO } from '@/service/types/usertypes';
import { useAppSelector, useAppDispatch, selectUser, setUser } from '@/store';
import { cn } from '@/utils/cn';

import type { User } from '@/store';
import type { PlantInfo } from '@/types';

// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────

export interface EditProfileFields {
  firstName: string;
  lastName: string;
  email: string;
  carrier: string[];
  plants: string[];
}

interface EditProfileDialogProps {
  isOpen: boolean;
  initialFields: EditProfileFields | null;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────
// Sub-components (matching Signup page dropdown style)
// ─────────────────────────────────────────────────────────────────

/** Resolve carrier IDs to display names */
function getCarrierNames(carrierIds: string[], carriers: CarrierDTO[]): string[] {
  return carrierIds.map((id) => {
    const found = carriers.find((c) => c._id === id);
    return found ? found.carrier_name : id;
  });
}

/** Resolve plant IDs to display names */
function getPlantNames(plantIds: string[], availablePlants: PlantInfo[]): string[] {
  return plantIds.map((id) => {
    const found = availablePlants.find((p) => p._id === id);
    return found ? found.plant_name : id;
  });
}

// ── Carrier Dropdown (single-select, search, ID-based) ──

interface CarrierDropdownProps {
  selectedCarrierId: string[];
  onSelectCarrier: (carrierId: string) => void;
  availableCarriers: CarrierDTO[];
  disabled?: boolean;
}

function CarrierDropdown({
  selectedCarrierId,
  onSelectCarrier,
  availableCarriers,
  disabled,
}: CarrierDropdownProps) {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredCarriers = useMemo(() => {
    if (!search.trim()) return availableCarriers;
    const q = search.toLowerCase().trim();
    return availableCarriers.filter((c) => c.carrier_name.toLowerCase().includes(q));
  }, [availableCarriers, search]);

  const selectedCarrierNames = useMemo(
    () => getCarrierNames(selectedCarrierId, availableCarriers),
    [selectedCarrierId, availableCarriers]
  );

  const inputValue = isDropdownOpen ? search : selectedCarrierNames.join(', ');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!isDropdownOpen) setIsDropdownOpen(true);
  };

  const handleFocus = () => {
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
      setSearch('');
    }
  };

  return (
    <div className="w-full" ref={dropdownRef}>
      <div
        className={cn(
          'flex h-auto w-full items-center bg-transparent px-3 py-3 text-base',
          'border-[1.8px] border-[#657E90] rounded-lg',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={t('editProfile.searchCarrier')}
          disabled={disabled}
          readOnly={!isDropdownOpen}
          className={cn(
            'flex-1 h-full bg-transparent outline-none text-[13.5px] text-[#212121] placeholder:text-[#636363] placeholder:text-[14.5px] placeholder:font-light placeholder:font-poppins min-w-0',
            !isDropdownOpen && 'truncate cursor-pointer'
          )}
          aria-label={t('editProfile.carrier')}
          autoComplete="off"
        />
        <ChevronDown
          className={cn(
            'h-6 w-6 text-gray-500 flex-shrink-0 ml-2 transition-transform cursor-pointer',
            isDropdownOpen && 'rotate-180'
          )}
          onClick={() => {
            if (!disabled) {
              if (isDropdownOpen) {
                setIsDropdownOpen(false);
                setSearch('');
              } else {
                setIsDropdownOpen(true);
                setSearch('');
                inputRef.current?.focus();
              }
            }
          }}
        />
      </div>

      {isDropdownOpen && (
        <div className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-48 overflow-y-auto p-1">
            {filteredCarriers.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {t('editProfile.noCarriersFound')}
              </div>
            ) : (
              filteredCarriers.map((c) => {
                const isSelected = selectedCarrierId.includes(c._id);
                return (
                  <button
                    key={c._id}
                    type="button"
                    onClick={() => {
                      onSelectCarrier(c._id);
                      setIsDropdownOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-3 py-2.5 text-sm text-left transition-colors',
                      isSelected ? 'bg-[#91c1e5]' : 'hover:bg-gray-100'
                    )}
                  >
                    <span className={cn('truncate', isSelected && 'font-medium text-black')}>
                      {c.carrier_name}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Plants Dropdown (multi-select, search, ID-based) ──

interface PlantsDropdownProps {
  selectedPlantIds: string[];
  onTogglePlant: (plantId: string) => void;
  availablePlants: PlantInfo[];
  disabled?: boolean;
}

function PlantsDropdown({
  selectedPlantIds,
  onTogglePlant,
  availablePlants,
  disabled,
}: PlantsDropdownProps) {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredPlants = useMemo(() => {
    if (!search.trim()) return availablePlants;
    const q = search.toLowerCase().trim();
    return availablePlants.filter(
      (p) => p.plant_name.toLowerCase().includes(q) || p.plant_code.toLowerCase().includes(q)
    );
  }, [availablePlants, search]);

  const selectedPlantsame = useMemo(
    () => getPlantNames(selectedPlantIds, availablePlants),
    [selectedPlantIds, availablePlants]
  );

  const inputValue = isDropdownOpen ? search : selectedPlantsame.join(', ');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!isDropdownOpen) setIsDropdownOpen(true);
  };

  const handleFocus = () => {
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
      setSearch('');
    }
  };

  return (
    <div className="w-full" ref={dropdownRef}>
      <div
        className={cn(
          'flex h-auto w-full items-center bg-transparent px-3 py-3 text-base',
          'border-[1.8px] border-[#657E90] rounded-lg',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <div className="flex-1 grid min-w-0">
          {!inputValue && (
            <span className="col-start-1 row-start-1 self-center text-[14.5px] text-[#636363] font-light font-poppins pointer-events-none">
              {t('editProfile.searchPlant')}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            disabled={disabled}
            readOnly={!isDropdownOpen}
            className={cn(
              'col-start-1 row-start-1 w-full self-center bg-transparent outline-none text-[13.5px] text-[#212121] min-w-0',
              !isDropdownOpen && 'truncate cursor-pointer'
            )}
            aria-label={t('editProfile.plants')}
            autoComplete="off"
          />
        </div>
        <ChevronDown
          className={cn(
            'font-bold h-6 w-6 text-gray-500 text-xl flex-shrink-0 ml-2 transition-transform cursor-pointer',
            isDropdownOpen && 'rotate-180'
          )}
          onClick={() => {
            if (!disabled) {
              if (isDropdownOpen) {
                setIsDropdownOpen(false);
                setSearch('');
              } else {
                setIsDropdownOpen(true);
                setSearch('');
                inputRef.current?.focus();
              }
            }
          }}
        />
      </div>

      {isDropdownOpen && (
        <div className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-48 overflow-y-auto p-1">
            {filteredPlants.length === 0 ? (
              <div className="px-3 py-4 text-base text-gray-500 text-center">
                {t('editProfile.noPlantsFound')}
              </div>
            ) : (
              filteredPlants.map((plant) => {
                const isSelected = selectedPlantIds.includes(plant._id);
                return (
                  <button
                    key={plant._id}
                    type="button"
                    onClick={() => {
                      onTogglePlant(plant._id);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between rounded-sm px-3 py-2.5 text-sm text-left transition-colors',
                      isSelected ? 'bg-[#cee0ed]' : 'hover:bg-gray-100'
                    )}
                  >
                    <span className={cn('truncate', isSelected && 'font-medium text-black')}>
                      {plant.plant_name}
                    </span>
                    {isSelected && (
                      <Check
                        className="h-4 w-4 text-[#025f9e] font-bold flex-shrink-0 ml-2"
                        strokeWidth={4}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const VALID_NAME_REGEX = /^(?=.*[a-zA-Z])[a-zA-Z'-]+$/;

function hasFieldsChanged(current: EditProfileFields, original: EditProfileFields): boolean {
  if (current.firstName.trim() !== original.firstName.trim()) return true;
  if (current.lastName.trim() !== original.lastName.trim()) return true;
  if (current.email.trim() !== original.email.trim()) return true;
  if (current.carrier !== original.carrier) return true;
  const currentPlants = [...current.plants].sort();
  const originalPlants = [...original.plants].sort();
  if (currentPlants.length !== originalPlants.length) return true;
  for (let i = 0; i < currentPlants.length; i++) {
    if (currentPlants[i] !== originalPlants[i]) return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────
// Main Dialog Component
// ─────────────────────────────────────────────────────────────────

export function EditProfileDialog({ isOpen, initialFields, onClose }: EditProfileDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const user = useAppSelector(selectUser);
  const availablePlants = useAppSelector((state) => state.global.plants);
  const carriers = useAppSelector((state) => state.global.carriers);
  const functionCodes = useAppSelector((state) => state.global.functionCodes);

  // Local form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [carrier, setCarrier] = useState<string[]>([]);
  const [plants, setPlants] = useState<string[]>([]);
  const [originalFields, setOriginalFields] = useState<EditProfileFields | null>(null);

  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const plantsQuery = usePlantsQuery();
  const carrierQuery = useCarriersQuery();

  const loading = plantsQuery.isFetching || carrierQuery.isFetching;

  useEffect(() => {
    if (isOpen) {
      void plantsQuery.refetch();
      void carrierQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Disable background scroll when dialog is open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Initialize fields when dialog opens
  useEffect(() => {
    if (isOpen && initialFields) {
      setFirstName(initialFields.firstName);
      setLastName(initialFields.lastName);
      setEmail(initialFields.email);
      setCarrier(initialFields.carrier);
      setPlants(initialFields.plants);
      setOriginalFields(initialFields);
      setStatus('idle');
      setErrorMessage(null);
      setFirstNameError(null);
      setLastNameError(null);
    }
  }, [isOpen, initialFields]);

  const fields: EditProfileFields = useMemo(
    () => ({ firstName, lastName, email, carrier, plants }),
    [firstName, lastName, email, carrier, plants]
  );

  const isUpdateEnabled = useMemo(() => {
    if (status === 'loading') return false;
    if (!originalFields) return false;
    return hasFieldsChanged(fields, originalFields);
  }, [fields, originalFields, status]);

  const emailError = useMemo(() => {
    const trimmed = email.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return t('editProfile.validEmail');
    }
    return null;
  }, [email, t]);

  const handleTogglePlant = useCallback((plantId: string) => {
    setPlants((prev) => {
      const index = prev.indexOf(plantId);
      if (index >= 0) {
        return prev.filter((id) => id !== plantId);
      }
      return [...prev, plantId];
    });
  }, []);

  const handleSelectCarrier = useCallback((carrierId: string) => {
    setCarrier([carrierId]);
  }, []);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleUpdate = useCallback(async () => {
    if (!isUpdateEnabled || status === 'loading') return;

    if (!user) return;

    // Validate required fields
    const missingFields: string[] = [];
    if (!firstName.trim()) missingFields.push(t('editProfile.firstName'));
    if (!lastName.trim()) missingFields.push(t('editProfile.lastName'));
    if (plants.length === 0) missingFields.push(t('editProfile.plants'));
    if (carrier.length === 0) missingFields.push(t('editProfile.carrier'));

    if (missingFields.length > 0) {
      showToast(
        `${missingFields.join(', ')} ${missingFields.length > 1 ? t('signup.areRequired') : t('signup.isRequired')}`,
        {
          type: 'error',
        }
      );
      return;
    }
    const firstNameInvalid = firstName.trim() !== '' && !VALID_NAME_REGEX.test(firstName.trim());
    const lastNameInvalid = lastName.trim() !== '' && !VALID_NAME_REGEX.test(lastName.trim());
    setFirstNameError(firstNameInvalid ? t('editProfile.invalidName') : null);
    setLastNameError(lastNameInvalid ? t('editProfile.invalidName') : null);
    if (firstNameInvalid || lastNameInvalid) {
      showToast(t('editProfile.invalidName'), { type: 'error' });
      return;
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      showToast(t('editProfile.validEmail'), { type: 'error' });
      return;
    }

    setStatus('loading');
    setErrorMessage(null);

    try {
      const functionCode = functionCodes?.users ?? '';
      if (!functionCode) {
        setStatus('error');
        setErrorMessage(t('editProfile.functionCodeNotAvailable'));
        return;
      }

      const request: UpdateUserProfileRequestDTO = {
        _id: user._id,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        country_code: user.country_code,
        mobile_no: user.mobile_no,
        email: email.trim(),
        carrier,
        plants,
      };

      const response = await updateUserProfile(request);

      if (!response.status) {
        setStatus('error');
        setErrorMessage(response.message || t('editProfile.failedToUpdate'));
        return;
      }

      // Resolve plant IDs back to PlantInfo objects
      const resolvedPlants = plants
        .map((id) => availablePlants.find((p) => p._id === id))
        .filter((p): p is PlantInfo => p !== undefined);

      const resolvedCarrier = carrier
        .map((id) => carriers.find((c) => c._id === id))
        .filter((c): c is CarrierDTO => c !== undefined);

      const updatedUser: User = {
        ...user,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        carrier: resolvedCarrier,
        plants: resolvedPlants,
        login_date_time: user.login_date_time,
      };

      // Update Redux auth user
      dispatch(setUser(updatedUser));
      showToast(t('editProfile.profileUpdatedSuccess'));
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('editProfile.failedToUpdate');
      setStatus('error');
      setErrorMessage(message);
      showToast(message, { type: 'error' });
    }
  }, [
    isUpdateEnabled,
    status,
    user,
    firstName,
    lastName,
    email,
    carrier,
    plants,
    functionCodes,
    availablePlants,
    carriers,
    dispatch,
    showToast,
    onClose,
    t,
  ]);

  const phoneDisplay = useMemo(() => {
    if (!user) return '';
    return `(${user.country_code}) ${user.mobile_no}`;
  }, [user]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-transparent flex items-center justify-center py-4"
      role="button"
      tabIndex={0}
      onClick={handleCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleCancel();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="relative justify-center 3xl:w-[38vw] 2xl:w-[48vw] xl:w-[48vw] w-[70vw] max-h-[90vh] flex flex-col bg-[#f7f7f7] rounded-lg sm:rounded-xl shadow-xl p-3 sm:p-4 md:p-4 border-[#e2e1e1] border-[0.8px]"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {loading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/60 rounded-lg sm:rounded-xl">
            <Loader2 className="h-12 w-12 text-[#1457A0] animate-spin mb-4" />
            <p className="text-gray-500">
              {t('plant.selection.loading', 'Loading plants and carriers...')}
            </p>
          </div>
        )}
        {/* Title */}
        <h2 className="px-2 text-xl font-semibold font-poppins text-[#0B2F50]">
          {t('editProfile.title')}
        </h2>

        {/* Error message */}
        {errorMessage && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 text-red-700 text-xs p-3 mb-4">
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Scrollable form area */}
        <div className="px-4 pt-6 flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-[2px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-track]:bg-transparent">
          <div className="font-poppins space-y-3 sm:space-y-4">
            {/* First Name */}
            <div>
              <label
                htmlFor="edit-firstName"
                className="block text-[15px] font-medium font-poppins text-[#647d8f] mb-1"
              >
                {t('editProfile.firstName')}
                <span className="text-[#3e4f59] font-semibold"> *</span>
              </label>
              <input
                id="edit-firstName"
                type="text"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value.replace(/[^a-zA-Z'-]/g, ''));
                  if (firstNameError) setFirstNameError(null);
                }}
                placeholder={t('editProfile.enterFirstName')}
                maxLength={20}
                disabled={status === 'loading'}
                className={cn(
                  'flex h-auto w-full rounded-lg border-[1.8px] bg-transparent px-3 py-3 text-[13px] text-[#212121] placeholder:text-[#425b6e] placeholder:text-[13px] placeholder:font-medium placeholder:font-poppins focus-visible:outline-none',
                  firstNameError ? 'border-red-500' : 'border-[#657E90]'
                )}
              />
              {firstNameError && <p className="mt-1 text-xs text-red-500">{firstNameError}</p>}
            </div>

            {/* Last Name */}
            <div>
              <label
                htmlFor="edit-lastName"
                className="block text-[15px] font-medium font-poppins text-[#647d8f] mb-1"
              >
                {t('editProfile.lastName')}
                <span className="text-[#3e4f59] font-semibold"> *</span>
              </label>
              <input
                id="edit-lastName"
                type="text"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value.replace(/[^a-zA-Z'-]/g, ''));
                  if (lastNameError) setLastNameError(null);
                }}
                placeholder={t('editProfile.enterLastName')}
                maxLength={20}
                disabled={status === 'loading'}
                className={cn(
                  'flex h-auto w-full rounded-lg border-[1.8px] bg-transparent px-3 py-3 text-[13px] text-[#212121] placeholder:text-[#425b6e] placeholder:text-[13px] placeholder:font-medium placeholder:font-poppins focus-visible:outline-none',
                  lastNameError ? 'border-red-500' : 'border-[#657E90]'
                )}
              />
              {lastNameError && <p className="mt-1 text-xs text-red-500">{lastNameError}</p>}
            </div>

            {/* Mobile Number - Non-editable */}
            <div>
              <label
                htmlFor="edit-phone"
                className="block text-[15px] font-medium font-poppins text-[#647d8f] mb-1"
              >
                {t('editProfile.mobileNumber')}
                <span className="text-[#3e4f59] font-semibold"> *</span>
              </label>
              <input
                id="edit-phone"
                type="tel"
                value={phoneDisplay}
                disabled
                className="flex h-auto w-full bg-[#aaaaaa] text-[#000000] rounded-lg border-[1.8px] disabled:opacity-100 border-[#657E90] px-3 py-3 font-normal text-[15px] placeholder:text-[#425b6e] placeholder:text-[13px] placeholder:font-medium placeholder:font-poppins focus-visible:outline-none"
              />
            </div>

            {/* Plant Selection */}
            <div className="relative">
              <label
                htmlFor="edit-plants"
                className="block text-[15px] font-medium font-poppins text-[#647d8f] mb-1"
              >
                {t('editProfile.plants')}
                <span className="text-[#3e4f59] font-semibold"> *</span>
              </label>
              <PlantsDropdown
                selectedPlantIds={plants}
                onTogglePlant={handleTogglePlant}
                availablePlants={availablePlants}
                disabled={status === 'loading'}
              />
            </div>

            {/* Carrier Selection */}
            <div className="relative">
              <label
                htmlFor="edit-carrier"
                className="block text-[15px] font-medium font-poppins text-[#647d8f] mb-1"
              >
                {t('editProfile.carrier')}
                <span className="text-[#3e4f59] font-semibold"> *</span>
              </label>
              <CarrierDropdown
                selectedCarrierId={carrier}
                onSelectCarrier={handleSelectCarrier}
                availableCarriers={carriers}
                disabled={status === 'loading'}
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="edit-email"
                className="block text-[15px] font-medium font-poppins text-[#647d8f] mb-1"
              >
                {t('editProfile.email')}{' '}
                <span className="text-[15px] font-medium font-poppins text-[#647d8f]">
                  {t('editProfile.emailOptional')}
                </span>
              </label>
              <input
                id="edit-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                placeholder={t('editProfile.enterEmail')}
                disabled={status === 'loading'}
                className="flex h-auto w-full rounded-lg border-[1.8px] border-[#657E90] bg-transparent px-3 py-3 text-[13px] text-[#212121] placeholder:text-[#425b6e] placeholder:text-[13px] placeholder:font-medium placeholder:font-poppins focus-visible:outline-none"
              />
              {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-2 md:gap-3 justify-end pt-2">
          <Button
            variant="outline"
            className="bg-[#f7f7f7] hover:bg-[#f7f7f7] border-[1.35px] h-auto py-[11px] px-16 border-[#003366] rounded-mdg text-[12px] text-[#104070] font-medium"
            onClick={handleCancel}
            disabled={status === 'loading'}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className={`h-auto py-[11px] px-16  border-[1.3px] border-[#ababab] text-white text-[12px] font-medium rounded-md ${isUpdateEnabled ? 'bg-[#003366] hover:bg-[#003366]' : 'bg-[#ababab]'}`}
            onClick={() => {
              void handleUpdate();
            }}
            disabled={!isUpdateEnabled}
            isLoading={status === 'loading'}
            loadingText={t('common.updating')}
          >
            {t('common.update')}
          </Button>
        </div>
      </div>
    </div>
  );
}
